import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player/lazy';
import { MediaStates } from '../../commands/practitioner/enums/MediaStates';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import BackendService from '../../services/BackendService';
import DialogMediaPlayer from '../DialogMediaPlayer/DialogMediaPlayer';
import { MediaTypes } from '../../commands/practitioner/enums/MediaTypes';
import SoundAnimation from './SoundAnimation';
import useClientMediaState from '../../hooks/useClientMediaState/useClientMediaState';
import { PLayerTriggeredBy } from '../../commands/practitioner/enums/PLayerTriggeredBy';
import { PlaybackType } from '../../commands/practitioner/enums/PlaybackType';
import { Box } from '@mui/system';
import { ClientMediaPlayerSXStyles } from './style.';

interface ClientMediaPlayerProps {
  mediaJumpEvent?: {
    seekMs: number;
    eventId: number;
  } | null;
}

const ClientMediaPlayer: React.FC<ClientMediaPlayerProps> = ({ mediaJumpEvent = null }) => {
  const hasReported = useRef(false);
  const failureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { ClientMediaPlayerSx } = ClientMediaPlayerSXStyles;
  const { sessionId, treatmentId } = useAppContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  const {
    clientMediaURI,
    isClientPlayerVisible,
    isClientReactPLayerStarted,
    clientMediaType,
    isMediaLoadedForClient,
    isUploadedTab,
  } = useClientMediaState();
  const [isImageValid, setIsImageValid] = useState(false);

  useEffect(() => {
    if (clientMediaType === MediaTypes.IMAGE && clientMediaURI) {
      const img = new Image();
      img.src = clientMediaURI;

      img.onload = () => setIsImageValid(true);
      img.onerror = () => setIsImageValid(false);
    } else {
      setIsImageValid(false);
    }
  }, [clientMediaURI, clientMediaType]);

  const canBePlayed = useMemo(() => {
    if (isUploadedTab) {
      return true;
    }

    switch (clientMediaType) {
      case MediaTypes.VIDEO:
      case MediaTypes.AUDIO:
        return ReactPlayer.canPlay(clientMediaURI);
      case MediaTypes.IMAGE:
        return isImageValid;
      default:
        return false;
    }
  }, [clientMediaURI, clientMediaType, isUploadedTab, isImageValid]);

  const reactPlayerClient = useMemo(
    () => ({
      height: '100%',
      playing: isPlaying,
      url: clientMediaURI,
      width: '100%',
      onPlay: () => {},
      onPause: () => {},
      onReady: () => setIsPlayerReady(true),
      controls: false,
      config: {
        youtube: {
          playerVars: {
            controls: 1,
            showinfo: 0,
            rel: 0,
            iv_load_policy: 3,
          },
        },
      },
    }),
    [isPlaying, clientMediaURI]
  );

  useEffect(() => {
    if (isClientReactPLayerStarted === PlaybackType.Skip) {
      setIsPlaying(false);
      return;
    }
    setIsPlaying(!isClientReactPLayerStarted);
  }, [isClientReactPLayerStarted]);

  useEffect(() => {
    if (!clientMediaURI || clientMediaURI.trim() === '') {
      return;
    }
    // Reset `hasReported` and clear the previous timeout when any of the dependencies change
    hasReported.current = false;
    if (failureTimeoutRef.current) {
      clearTimeout(failureTimeoutRef.current);
    }
    const checkMediaStatus = () => {
      if (canBePlayed && !!clientMediaURI && isMediaLoadedForClient) {
        if (!hasReported.current) {
          BackendService.sendMediaStatusReport(sessionId, treatmentId, clientMediaURI, MediaStates.Successful, 1.0);
          hasReported.current = true;
        }
      }
    };

    checkMediaStatus();

    // if waiting more then 10 seconds, we are sending failure report.
    if (!hasReported.current) {
      failureTimeoutRef.current = setTimeout(() => {
        if (!hasReported.current) {
          BackendService.sendMediaStatusReport(sessionId, treatmentId, clientMediaURI, MediaStates.Failure, 1.0);
          hasReported.current = true;
        }
      }, 8000);
    }

    return () => {
      if (failureTimeoutRef.current) {
        clearTimeout(failureTimeoutRef.current);
      }
    };
  }, [canBePlayed, clientMediaURI, isMediaLoadedForClient, sessionId, treatmentId, clientMediaType]);

  const stopPlayerWhenEndsHandler = () => {
    setIsPlaying(false);
  };
  return (
    <Box sx={ClientMediaPlayerSx} style={{ zIndex: 1 }} data-testid="pwid-client-media-container">
      {isMediaLoadedForClient && !!clientMediaURI && canBePlayed ? (
        <Box
          className={clientMediaType !== MediaTypes.IMAGE ? 'player' : 'image'}
          data-testid={clientMediaType !== MediaTypes.IMAGE ? 'pwid-client-media-player' : 'pwid-client-media-image'}
          style={{
            display: isClientPlayerVisible ? 'flex' : 'none',
          }}
        >
          {clientMediaType === MediaTypes.AUDIO && isPlaying && isPlayerReady ? <SoundAnimation /> : null}
          <DialogMediaPlayer
            ReactPlayerProps={reactPlayerClient}
            ReactPlayerTriggeredBy={PLayerTriggeredBy.Client}
            handleMediaEnd={stopPlayerWhenEndsHandler}
            seekToMs={mediaJumpEvent?.seekMs}
            seekEventId={mediaJumpEvent?.eventId}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default ClientMediaPlayer;
